import { supabase } from "@/integrations/supabase/client";
import { ParsedOrder } from "./bulkOrderUtils";

export interface DuplicateCheckResult {
  internalDuplicates: Array<{index: number, reason: string, duplicateOf: number}>;
  databaseDuplicates: Array<{index: number, existingOrderId: string, existingOrder: any, reason: string}>;
}

/**
 * Detects duplicates both within the batch and against existing database orders
 */
export async function detectDuplicates(
  orders: ParsedOrder[],
  userId: string
): Promise<DuplicateCheckResult> {
  const internalDuplicates: Array<{index: number, reason: string, duplicateOf: number}> = [];
  const databaseDuplicates: Array<{index: number, existingOrderId: string, existingOrder: any, reason: string}> = [];

  // 1. Check for internal duplicates (within the batch)
  const seenOrderNumbers = new Map<string, number>(); // orderNumber -> first occurrence index
  
  orders.forEach((order, index) => {
    if (order.orderNumber && order.orderNumber.trim()) {
      const orderNum = order.orderNumber.trim().toLowerCase();
      
      if (seenOrderNumbers.has(orderNum)) {
        const firstIndex = seenOrderNumbers.get(orderNum)!;
        internalDuplicates.push({
          index,
          reason: `Duplicate order #${order.orderNumber} in this batch (first appears at row ${firstIndex + 1})`,
          duplicateOf: firstIndex
        });
      } else {
        seenOrderNumbers.set(orderNum, index);
      }
    }
  });

  // 2. Check against database (only for non-archived orders with order numbers)
  const ordersWithNumbers = orders
    .map((order, index) => ({ order, index }))
    .filter(({order}) => order.orderNumber && order.orderNumber.trim());

  if (ordersWithNumbers.length > 0) {
    const orderNumbers = ordersWithNumbers.map(({order}) => order.orderNumber!.trim());
    
    try {
      const { data: existingOrders, error } = await supabase
        .from('orders')
        .select('id, order_number, customer_name, customer_phone, parsed_items, total_price, created_at')
        .eq('user_id', userId)
        .eq('is_archived', false)
        .in('order_number', orderNumbers);

      if (error) {
        console.error('Database duplicate check error:', error);
      } else if (existingOrders && existingOrders.length > 0) {
        // Map existing order numbers to their full data
        const existingMap = new Map(
          existingOrders.map(o => [o.order_number?.toLowerCase(), o])
        );

        ordersWithNumbers.forEach(({order, index}) => {
          const orderNum = order.orderNumber!.trim().toLowerCase();
          if (existingMap.has(orderNum)) {
            const existingOrder = existingMap.get(orderNum)!;
            databaseDuplicates.push({
              index,
              existingOrderId: existingOrder.id,
              existingOrder: existingOrder,
              reason: `Order #${order.orderNumber} already exists in your database`
            });
          }
        });
      }
    } catch (error) {
      console.error('Error checking database duplicates:', error);
    }
  }

  return { internalDuplicates, databaseDuplicates };
}

/**
 * Marks orders as duplicates based on detection results
 */
export function markDuplicates(
  orders: ParsedOrder[],
  duplicateResult: DuplicateCheckResult
): ParsedOrder[] {
  const updatedOrders = [...orders];

  // Mark internal duplicates
  duplicateResult.internalDuplicates.forEach(({index, reason, duplicateOf}) => {
    updatedOrders[index] = {
      ...updatedOrders[index],
      isDuplicate: true,
      duplicateReason: reason,
      duplicateOf: `batch-${duplicateOf}`,
      rowState: 'duplicate',
      errors: [
        ...updatedOrders[index].errors,
        { field: 'orderNumber', message: reason }
      ]
    };
  });

  // Mark database duplicates
  duplicateResult.databaseDuplicates.forEach(({index, existingOrderId, existingOrder, reason}) => {
    updatedOrders[index] = {
      ...updatedOrders[index],
      isDuplicate: true,
      duplicateReason: reason,
      duplicateOf: existingOrderId,
      existingOrderData: existingOrder,
      rowState: 'duplicate',
      errors: [
        ...updatedOrders[index].errors,
        { field: 'orderNumber', message: reason }
      ]
    };
  });

  return updatedOrders;
}